import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  fetchBootstrap,
  updatePerfilGlobal as sdkUpdate,
  recomputeProgreso as sdkRecompute,
  uploadAvatar as sdkUploadAvatar,
} from "../lib/profile";
import { useAuth } from "./AuthContext";

/**
 * ProfileContext — fuente única de verdad del Perfil Global + Progreso en el cliente.
 *
 * Se monta al aterrizar en /mi-perfil y NO se replica en cada página; los sub-módulos
 * consumen useProfile() para leer/actualizar sin duplicar requests.
 *
 * Alineado con Etapa 4 §2.5 (contextos) y §2.6 (estado servidor).
 */

const ProfileContext = createContext(null);

export function ProfileProvider({ children }) {
  const { user } = useAuth();
  const [perfilGlobal, setPerfilGlobal] = useState(null);
  const [progreso, setProgreso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bootstrap = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchBootstrap();
      setPerfilGlobal(data.perfil_global);
      setProgreso(data.progreso);
    } catch (e) {
      setError(e);
      // Fallback degradado: si el bootstrap falla (timeout / error de datos en
      // servidor), construimos un Perfil Global mínimo desde la sesión para que
      // la pantalla NUNCA quede atrapada en el esqueleto de carga.
      setPerfilGlobal((prev) => prev || {
        user_id: user.id,
        correo: user.email,
        nombre_completo: user.name || "",
        foto_url: user.avatar || null,
        tipo_usuario: user.role || "cliente",
        telefono: "",
        ubicacion: null,
        descripcion_corta: "",
        verificado: {},
        version: null,
        _fallback: true,
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const updateGlobal = useCallback(
    async (patch) => {
      if (!perfilGlobal) return null;
      const updated = await sdkUpdate(patch, perfilGlobal.version);
      setPerfilGlobal(updated);
      // Progreso se recalcula del lado servidor por evento; refrescamos.
      try {
        const fresh = await sdkRecompute();
        setProgreso(fresh);
      } catch (_) {
        /* ignore, backend event bus lo recomputó igual */
      }
      return updated;
    },
    [perfilGlobal]
  );

  const refreshProgreso = useCallback(async () => {
    try {
      const fresh = await sdkRecompute();
      setProgreso(fresh);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const uploadAvatar = useCallback(async (file, onProgress) => {
    const updated = await sdkUploadAvatar(file, onProgress);
    setPerfilGlobal(updated);
    try {
      const fresh = await sdkRecompute();
      setProgreso(fresh);
    } catch (_) {
      /* ignore */
    }
    return updated;
  }, []);

  const value = {
    perfilGlobal,
    progreso,
    loading,
    error,
    refresh: bootstrap,
    refreshProgreso,
    updateGlobal,
    uploadAvatar,
  };

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
}

export function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) {
    throw new Error("useProfile debe usarse dentro de <ProfileProvider>");
  }
  return ctx;
}
